import { DOCUMENT_FALLBACKS } from './documentFallbacks'

function toPlainText(value: unknown) {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

export function safeText(value: unknown, fallback = DOCUMENT_FALLBACKS.notProvided) {
  const text = toPlainText(value).trim()
  return text.length > 0 ? text : fallback
}

export function safeVin(value: unknown, fallback = DOCUMENT_FALLBACKS.missing) {
  const text = toPlainText(value).trim().toUpperCase()
  return text.length > 0 ? text : fallback
}

export function shortId(value: unknown, fallback = DOCUMENT_FALLBACKS.unknown) {
  const text = toPlainText(value).trim()
  return text.length > 0 ? text.slice(0, 8) : fallback
}

export function yesNo(value: unknown, fallback = DOCUMENT_FALLBACKS.pending) {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return fallback
}

export function formatMoney(value: unknown, currency = 'USD', fallback = DOCUMENT_FALLBACKS.tbd) {
  const amount = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(amount)) return fallback

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function formatDate(value: unknown, fallback = DOCUMENT_FALLBACKS.tbd) {
  if (!value) return fallback
  const date = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(date.getTime())) return fallback
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

export function formatAddress(value: unknown, fallback = DOCUMENT_FALLBACKS.notProvided) {
  if (typeof value === 'string') {
    const text = value.trim()
    return text.length > 0 ? text : fallback
  }

  if (value && typeof value === 'object') {
    const address = value as Record<string, unknown>
    const parts = [
      address.street1,
      address.street2,
      address.city && address.state ? `${address.city}, ${address.state}` : address.city ?? address.state,
      address.zip,
      address.country,
    ]
      .map((part) => safeText(part, ''))
      .filter(Boolean)

    return parts.length > 0 ? parts.join('\n') : fallback
  }

  return fallback
}

export function formatPhone(value: unknown, fallback = DOCUMENT_FALLBACKS.contactDispatcher) {
  const text = safeText(value, '')
  const digits = text.replace(/\D+/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return text || fallback
}
